import React, { useEffect, useState } from "react";
import "./FrontPage.css";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";

import FrontPageLogo from "../../components/Logo/Logo";
import LanguageSelector from "../../components/LanguageSelector/LanguageSelector";
import HowToPlayPreview from "../../components/HowToPlayPreview/HowToPlayPreview";
import AnnouncementBanner from "../../components/AnnouncementBanner/AnnouncementBanner";
import CommunityStatsBar from "../../components/CommunityStatsBar/CommunityStatsBar";
import GalleryTeaser from "../../components/GalleryTeaser/GalleryTeaser";
import AnalyticsTeaser from "../../components/AnalyticsTeaser/AnalyticsTeaser";
import LeaderboardTeaser from "../../components/LeaderboardTeaser/LeaderboardTeaser";
import CommunityApi from "../../shared/api/CommunityApi";
import {
  CommunityStats,
  LeaderboardEntry,
  SocialLink,
} from "../../shared/interfaces/GameLogicTypes";
import useRank5Auth from "../../shared/useRank5Auth";

export default function FrontPage() {
  const { isAuthenticated, loginWithRedirect } = useRank5Auth();
  const history = useHistory();
  const { t } = useTranslation();

  const [stats, setStats] = useState<CommunityStats | null>(null);
  const [topCreators, setTopCreators] = useState<LeaderboardEntry[] | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);

  useEffect(() => {
    CommunityApi.getStats()
      .then(setStats)
      .finally(() => setLoadingStats(false));
    CommunityApi.getTopCreators().then((res) => setTopCreators(res?.creators ?? null));
    CommunityApi.getSocialLinks().then(setSocialLinks);
  }, []);

  return (
    <main
      className="frontpage-root"
    >
      {/* ─── Hero ─── */}
      <section className="frontpage-hero" style={{ position: "relative" }} aria-labelledby="frontpage-title">
        {socialLinks.length > 0 && (
          <Box sx={{ position: "absolute", top: 16, left: 16, display: "flex", gap: 0.5 }}>
            {socialLinks.map((link) => (
              <Tooltip key={link.id} title={link.label} arrow>
                <IconButton
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{
                    color: "rgba(0,0,0,0.45)",
                    "&:hover": { color: "#ec7777" },
                    "& svg": { width: 22, height: 22 },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ display: "flex", "& svg": { width: 22, height: 22 } }}
                    dangerouslySetInnerHTML={{ __html: link.svgMarkup }}
                  />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )}
        <FrontPageLogo party="full" />
        <Typography
          id="frontpage-title"
          component="h1"
          className="frontpage-hero-tagline"
          sx={{ mt: 1, mb: 2 }}
        >
          {t("frontpage_tagline", "Find out what your friends think of you")}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => history.push("/play")}
          sx={{
            px: 5,
            py: 1.5,
            fontSize: "1.1rem",
            borderRadius: 3,
            textTransform: "none",
            mb: 2,
          }}
        >
          {t("go_to_play")}
        </Button>
        <HowToPlayPreview />
        <div className="frontpage-hero-links">
          <a href="/howTo">{t("go_to_how_to_play")}</a>
          <a href="/faq">{t("go_to_FAQ")}</a>
          <a href="/feedback">{t("give_feedback")}</a>
        </div>
      </section>

      {/* ─── Announcement ─── */}
      <section className="frontpage-section" style={{ marginBottom: 24 }} aria-label="Announcements">
        <AnnouncementBanner />
      </section>

      {/* ─── Community Stats ─── */}
      <section className="frontpage-section" style={{ marginBottom: 32 }} aria-label="Rank5 community statistics">
        {loadingStats ? (
          <Skeleton variant="rounded" height={80} sx={{ borderRadius: 3 }} />
        ) : (
          <CommunityStatsBar stats={stats} />
        )}
      </section>

      {/* ─── Community Teasers ─── */}
      <section className="frontpage-section" style={{ marginBottom: 16 }} aria-label="Explore the Rank5 community">
        <div className="frontpage-teasers">
          <AnalyticsTeaser />
          <LeaderboardTeaser creators={topCreators} />
          <GalleryTeaser />
        </div>
      </section>

      <div className="frontpage-section" style={{ textAlign: "center", marginBottom: 32 }}>
        <Button
          variant="outlined"
          onClick={() => history.push("/community")}
          sx={{ borderRadius: 3, textTransform: "none", px: 3 }}
        >
          {t("frontpage_explore_community", "Explore Community")}
        </Button>
      </div>

      {/* ─── Creator CTA ─── */}
      <section className="frontpage-cta" aria-labelledby="frontpage-creator-title">
        <Typography id="frontpage-creator-title" component="h2" variant="h5" fontWeight="bold">
          {t("frontpage_cta_title", "Be a creator")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("frontpage_cta_subtitle", "Contribute to the game — create your own deck")}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            onClick={() => history.push("/profile")}
            sx={{ borderRadius: 3, textTransform: "none" }}
          >
            {t("frontpage_create_deck", "Create a Deck")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (isAuthenticated) {
                history.push("/profile");
              } else {
                loginWithRedirect({ appState: { returnTo: "/profile" } });
              }
            }}
            sx={{ borderRadius: 3, textTransform: "none" }}
          >
            {t("frontpage_profile", "Profile")}
          </Button>
        </Stack>
      </section>

      {/* ─── Language Selector ─── */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 100,
        }}
      >
        <LanguageSelector />
      </Box>

      {/* ─── Footer ─── */}
      <footer className="frontpage-footer">
        <a href="/howTo">{t("go_to_how_to_play")}</a>
        <a href="/faq">{t("go_to_FAQ")}</a>
        <a href="/community">{t("frontpage_community", "Community")}</a>
        <a href="/feedback">{t("give_feedback")}</a>
        <a
          href="/profile"
          onClick={(e) => {
            e.preventDefault();
            if (isAuthenticated) {
              history.push("/profile");
            } else {
              loginWithRedirect({ appState: { returnTo: "/profile" } });
            }
          }}
        >
          Profile
        </a>
      </footer>
    </main>
  );
}
